/**
 * TanStack Query hooks for WorkspaceSessions
 *
 * Provides query and mutation hooks for workspace session management.
 * These hooks use TanStack Query for caching, background updates, and optimistic updates.
 *
 * @module hooks/useWorkspaceSessions
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ============================================
// Constants
// ============================================

const API_BASE = '/api/workspace-sessions';
const QUERY_KEY = ['workspace-sessions'];
const STALE_TIME = 60 * 1000; // 60 seconds - data is fresh for 1 minute

// ============================================
// API Functions
// ============================================

/**
 * Fetch all workspace sessions for the current user
 */
async function fetchSessions() {
  const response = await fetch(API_BASE, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch sessions: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch a single workspace session by ID
 */
async function fetchSession(id) {
  const response = await fetch(`${API_BASE}/${id}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch session: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Create a new workspace session
 */
async function createSession(sessionData) {
  const response = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(sessionData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create session: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Update a workspace session
 */
async function updateSession({ id, ...updates }) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to update session: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Delete a workspace session
 */
async function deleteSession(id) {
  const response = await fetch(`${API_BASE}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to delete session: ${response.statusText}`);
  }

  return { id };
}

/**
 * Set a session as default
 */
async function setDefaultSession(id) {
  const response = await fetch(`${API_BASE}/${id}/set-default`, {
    method: 'PUT',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to set default: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Create a session from a project
 */
async function createFromProject(projectId) {
  const response = await fetch(`${API_BASE}/from-project/${projectId}`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create from project: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Add an agent to a session
 */
async function addAgentToSession({ sessionId, agentId }) {
  const response = await fetch(`${API_BASE}/${sessionId}/agents/${agentId}`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to add agent: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Remove an agent from a session
 */
async function removeAgentFromSession({ sessionId, agentId }) {
  const response = await fetch(`${API_BASE}/${sessionId}/agents/${agentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to remove agent: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Add an agent to split view
 */
async function addAgentToSplit({ sessionId, agentId }) {
  const response = await fetch(`${API_BASE}/${sessionId}/split/${agentId}`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to add agent to split: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

/**
 * Remove an agent from split view
 */
async function removeAgentFromSplit({ sessionId, agentId }) {
  const response = await fetch(`${API_BASE}/${sessionId}/split/${agentId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to remove agent from split: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data || data;
}

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch all workspace sessions
 *
 * @param {object} options - Additional query options
 * @returns {object} Query result with sessions data
 */
export function useWorkspaceSessions(options = {}) {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSessions,
    staleTime: STALE_TIME,
    ...options,
  });
}

/**
 * Hook to fetch a single workspace session
 *
 * @param {string} id - Session ID
 * @param {object} options - Additional query options
 * @returns {object} Query result with session data
 */
export function useWorkspaceSessionById(id, options = {}) {
  return useQuery({
    queryKey: [...QUERY_KEY, id],
    queryFn: () => fetchSession(id),
    staleTime: STALE_TIME,
    enabled: !!id,
    ...options,
  });
}

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new workspace session
 *
 * @returns {object} Mutation object with mutate function
 */
export function useCreateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSession,
    onSuccess: (newSession) => {
      // Add the new session to the cache
      queryClient.setQueryData(QUERY_KEY, (old = []) => [...old, newSession]);
    },
  });
}

/**
 * Hook to update a workspace session
 *
 * @returns {object} Mutation object with mutate function
 */
export function useUpdateSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSession,
    onMutate: async ({ id, ...updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      // Snapshot previous value
      const previousSessions = queryClient.getQueryData(QUERY_KEY);

      // Optimistically update the cache
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session =>
          session.id === id ? { ...session, ...updates } : session
        )
      );

      // Return context with previous value
      return { previousSessions };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousSessions) {
        queryClient.setQueryData(QUERY_KEY, context.previousSessions);
      }
    },
    onSettled: () => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook to delete a workspace session
 *
 * @returns {object} Mutation object with mutate function
 */
export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSession,
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previousSessions = queryClient.getQueryData(QUERY_KEY);

      // Optimistically remove the session
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.filter(session => session.id !== id)
      );

      return { previousSessions };
    },
    onError: (err, variables, context) => {
      if (context?.previousSessions) {
        queryClient.setQueryData(QUERY_KEY, context.previousSessions);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook to set a session as default
 *
 * @returns {object} Mutation object with mutate function
 */
export function useSetDefaultSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setDefaultSession,
    onSuccess: (updatedSession) => {
      // Update all sessions - clear old default, set new one
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session => ({
          ...session,
          isDefault: session.id === updatedSession.id,
        }))
      );
    },
  });
}

/**
 * Hook to create a session from a project
 *
 * @returns {object} Mutation object with mutate function
 */
export function useCreateSessionFromProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFromProject,
    onSuccess: (newSession) => {
      queryClient.setQueryData(QUERY_KEY, (old = []) => [...old, newSession]);
    },
  });
}

/**
 * Hook to add an agent to a session
 *
 * @returns {object} Mutation object with mutate function
 */
export function useAddAgentToSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addAgentToSession,
    onSuccess: (updatedSession) => {
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session =>
          session.id === updatedSession.id ? updatedSession : session
        )
      );
    },
  });
}

/**
 * Hook to remove an agent from a session
 *
 * @returns {object} Mutation object with mutate function
 */
export function useRemoveAgentFromSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeAgentFromSession,
    onMutate: async ({ sessionId, agentId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previousSessions = queryClient.getQueryData(QUERY_KEY);

      // Optimistically remove the agent from the session
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session =>
          session.id === sessionId
            ? { ...session, agentIds: session.agentIds.filter(id => id !== agentId) }
            : session
        )
      );

      return { previousSessions };
    },
    onError: (err, variables, context) => {
      if (context?.previousSessions) {
        queryClient.setQueryData(QUERY_KEY, context.previousSessions);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

/**
 * Hook to add an agent to split view
 *
 * @returns {object} Mutation object with mutate function
 */
export function useAddAgentToSplit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addAgentToSplit,
    onSuccess: (updatedSession) => {
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session =>
          session.id === updatedSession.id ? updatedSession : session
        )
      );
    },
  });
}

/**
 * Hook to remove an agent from split view
 *
 * @returns {object} Mutation object with mutate function
 */
export function useRemoveAgentFromSplit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeAgentFromSplit,
    onMutate: async ({ sessionId, agentId }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      const previousSessions = queryClient.getQueryData(QUERY_KEY);

      // Optimistically remove the agent from split
      queryClient.setQueryData(QUERY_KEY, (old = []) =>
        old.map(session =>
          session.id === sessionId
            ? { ...session, splitAgentIds: (session.splitAgentIds || []).filter(id => id !== agentId) }
            : session
        )
      );

      return { previousSessions };
    },
    onError: (err, variables, context) => {
      if (context?.previousSessions) {
        queryClient.setQueryData(QUERY_KEY, context.previousSessions);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

// ============================================
// Utility Hooks
// ============================================

/**
 * Hook to invalidate and refetch all sessions
 *
 * @returns {function} Function to invalidate sessions cache
 */
export function useInvalidateSessions() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });
}

/**
 * Hook to prefetch sessions (useful for preloading)
 *
 * @returns {function} Function to prefetch sessions
 */
export function usePrefetchSessions() {
  const queryClient = useQueryClient();
  return () => queryClient.prefetchQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchSessions,
    staleTime: STALE_TIME,
  });
}

// Export query key for external use
export { QUERY_KEY as WORKSPACE_SESSIONS_QUERY_KEY };
